import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, MessageCircle, Share2, Globe, Lock, Calendar, Crown, CheckCircle, Settings, Camera, Palette } from 'lucide-react';
import GroupSettingsDropdown from './GroupSettingsDropdown';
import GroupCredibilityChip from './GroupCredibilityChip';
import AvatarSelectorModal from '../../../components/profile/AvatarSelectorModal';
import GroupBackgroundThemePicker from './GroupBackgroundThemePicker';
import { groupBackgroundStyle } from '../../../utils/groupBackgroundThemes';

const GroupHeader = ({
  group,
  isOwner,
  isMember,
  isAdmin,
  canEditGroupTheme = false,  // owner/admin/moderator -- broader than isOwner, matches post/mute permission tier
  postsCount = 0,
  membersCount = 0,
  avatarUrl = null,
  onShare,
  onEdit,
  onDelete,
  onReport,
  onLeave,
  onJoin,
  onInvite,
  onSelectAvatar,
  onSelectBackgroundTheme,
  isSavingTheme = false,
}) => {
  const { t } = useTranslation();
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  return (
    <div className="relative">
      <div className="h-64 relative overflow-hidden" style={groupBackgroundStyle(group?.background_theme)}>
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100/80 dark:to-gray-900/80" />

        <div className="absolute top-6 right-6 flex space-x-3 z-10">
          {/* Owners land here constantly (edit details, pricing, members) --
              a first-class visible button, not just a hidden item inside
              the "..." menu next to Share/Report/Leave. */}
          {isOwner && (
            <button
              onClick={onEdit}
              className="px-4 py-2 text-white bg-indigo-600/80 hover:bg-indigo-500 backdrop-blur-sm rounded-xl flex items-center gap-2 border border-indigo-400/30 font-medium"
            >
              <Settings className="h-4 w-4" />
              {t('groups.header.manageGroup')}
            </button>
          )}

          {/* Broader than "Manage Group" -- owner/admin/moderator can
              personalize the page theme, matching the same permission tier
              as posting and muting, not owner-only. */}
          {canEditGroupTheme && (
            <button
              onClick={() => setShowThemeModal(true)}
              className="px-4 py-2 text-white bg-black/20 backdrop-blur-sm hover:bg-black/40 rounded-xl flex items-center gap-2 border border-white/10"
            >
              <Palette className="h-4 w-4" />
              {t('groups.header.theme')}
            </button>
          )}

          <button
            onClick={onShare}
            className="px-4 py-2 text-gray-300 bg-black/20 backdrop-blur-sm hover:bg-black/40 rounded-xl flex items-center gap-2 border border-white/10"
          >
            <Share2 className="h-4 w-4" />
            {t('groups.header.share')}
          </button>

          <GroupSettingsDropdown
            group={group}
            isOwner={isOwner}
            isMember={isMember}
            isAdmin={isAdmin}
            onEdit={onEdit}
            onDelete={onDelete}
            onReport={onReport}
            onLeave={onLeave}
            onJoin={onJoin}
            onInvite={onInvite}
          />
        </div>
      </div>

      {/* Normal flow + negative margin (not `absolute -bottom-N`): this
          block's own height varies a lot with content (wrapped badges,
          description, stats row -- especially at mobile widths), and an
          absolutely-positioned box anchored by `bottom` computes its TOP
          edge as containerBottom - ownHeight, so taller content pushes the
          top edge UP, not down. At 375px that pushed the title into the
          fixed global nav's 0-64px band. Flow + negative margin only pulls
          the fixed `-mt-20` amount up regardless of how tall the block
          grows below that. */}
      <div className="-mt-20 relative z-10 px-4 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* flex-col on mobile: with `items-end` in a single row, a tall
              wrapped text column (long name/description/stats at narrow
              widths) drags the much-shorter fixed-size avatar down to the
              bottom of the row instead of anchoring it by the title -- a
              bug the previous absolute-positioned header hid by rendering
              off-screen. Stacked avatar-above-text reads naturally on
              mobile; sm:flex-row restores the side-by-side desktop look. */}
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-6">
            <div className="relative">
              <button
                type="button"
                onClick={() => isOwner && setShowAvatarModal(true)}
                disabled={!isOwner}
                className={`group w-32 h-32 rounded-3xl border-4 border-white dark:border-gray-800 shadow-2xl overflow-hidden bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center relative ${isOwner ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt={group?.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-4xl font-bold">{group?.name?.charAt(0)?.toUpperCase() || 'G'}</span>
                )}
                {isOwner && (
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-1">
                    <Camera className="w-6 h-6 text-white" />
                    <span className="text-white text-xs font-medium">{t('groups.header.changeAvatar')}</span>
                  </div>
                )}
              </button>
              <div className="absolute -bottom-2 -right-2 w-10 h-10 bg-green-500 rounded-full border-4 border-white dark:border-gray-800 flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full" />
              </div>
            </div>

            {isOwner && (
              <AvatarSelectorModal
                isOpen={showAvatarModal}
                onClose={() => setShowAvatarModal(false)}
                onSelect={(url) => {
                  onSelectAvatar?.(url);
                  setShowAvatarModal(false);
                }}
                currentAvatar={avatarUrl}
              />
            )}

            {canEditGroupTheme && (
              <GroupBackgroundThemePicker
                isOpen={showThemeModal}
                onClose={() => setShowThemeModal(false)}
                onSelect={(themeId) => {
                  onSelectBackgroundTheme?.(themeId);
                  setShowThemeModal(false);
                }}
                currentTheme={group?.background_theme}
                saving={isSavingTheme}
              />
            )}

            <div className="flex-grow pb-4 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight break-words">{group?.name || t('common.loading')}</h1>
                 <GroupCredibilityChip credibility={group?.credibility} />
                <div className="flex flex-wrap items-center gap-2">
                  {isOwner && (
                    <div className="flex items-center space-x-2 bg-yellow-100 dark:bg-yellow-500/20 backdrop-blur-sm px-3 py-1 rounded-full border border-orange-600 dark:border-yellow-500/30">
                      <Crown className="text-yellow-700 dark:text-yellow-400" size={16} />
                      <span className="text-yellow-800 dark:text-yellow-300 text-sm font-medium">{t('groups.header.owner')}</span>
                    </div>
                  )}
                  {isMember && !isOwner && (
                    <div className="flex items-center space-x-2 bg-green-100 dark:bg-green-500/20 backdrop-blur-sm px-3 py-1 rounded-full border border-green-400 dark:border-green-500/30">
                      <CheckCircle className="text-green-700 dark:text-green-400" size={16} />
                      <span className="text-green-800 dark:text-green-300 text-sm font-medium">{t('groups.header.member')}</span>
                    </div>
                  )}
                  {group?.privacy === 'public' ? (
                    <div className="flex items-center space-x-2">
                      <Globe className="text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-400/10 p-1.5 rounded-lg" size={20} />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('groups.header.public')}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Lock className="text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-400/10 p-1.5 rounded-lg" size={20} />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('groups.header.private')}</span>
                    </div>
                  )}
                </div>
              </div>

              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl leading-relaxed mb-4">
                {group?.description || t('groups.header.noDescription')}
              </p>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center space-x-2">
                  <Users className="text-blue-700 dark:text-blue-400" size={18} />
                  <span className="text-gray-900 dark:text-white font-medium">{membersCount}</span>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.header.members')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MessageCircle className="text-green-700 dark:text-green-400" size={18} />
                  <span className="text-gray-900 dark:text-white font-medium">{postsCount}</span>
                  <span className="text-gray-600 dark:text-gray-400 text-sm">{t('groups.header.posts')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="text-violet-700 dark:text-purple-400" size={18} />
                  <span className="text-gray-600 dark:text-gray-400 text-sm">
                    {t('groups.header.created', { date: new Date(group?.created_at || Date.now()).toLocaleDateString() })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>      
    </div>
  );
};

export default GroupHeader;
